import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Routes, Route, Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // SPRINT 9: i18n support
import { useClinicData, LEAD_STATUSES, LEAD_SOURCE_CHANNELS } from '../../context/ClinicDataContext';
import { useClinicScope } from '../../hooks/useClinicScope';
import { useClinicLeads } from '../../hooks/useClinicLeads';
import { useRBAC } from '../../context/RBACContext';
import { useAuth } from '../../context/AuthContext';
import { clearUnifiedAuth } from '../../utils/unifiedAuth';
import { usePackageEnforcement } from '../../context/PackageEnforcementContext';
import { useSuspensionEnforcement } from '../../context/SuspensionEnforcementContext';
import { SubscriptionProvider, useSubscription } from '../../context/SubscriptionContext'; // Sprint 10
import { useSalesReps } from '../../hooks/useSalesReps';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { OfferCard } from '../../components/OfferCard';
import { dateInputLang } from '../../utils/dateFormat'; // OFFERS-UX-FINAL-CLOSURE — locale-aware date input
import TrialBadge from '../../components/TrialBadge'; // Sprint 10
import TrialStartScreen from '../../components/TrialStartScreen'; // Sprint 10
import TrialExpiredScreen from '../../components/TrialExpiredScreen'; // Sprint 10
import TrialExpiryWarningModal from '../../components/TrialExpiryWarningModal'; // Sprint 10 Batch 1.4
import UsageLimitBanner from '../../components/UsageLimitBanner'; // Sprint 10 Batch 2.1
import UsageLimitModal from '../../components/UsageLimitModal'; // Sprint 10 Batch 2.1
import UpgradeModal from '../../components/UpgradeModal'; // Sprint 10 Batch 2.2
import UsageStatsCardNew from '../../components/UsageStatsCard'; // Sprint 10 Batch 2.1
import { 
  Users, UserPlus, FileText, TrendingUp, Clock, CheckCircle,
  ChevronRight, ChevronUp, ChevronDown, LogOut, Building2, Filter, Search, AlertCircle,
  Settings, Stethoscope, DollarSign, LayoutDashboard, UserCog,
  RefreshCw, Zap, Globe, Award, Image as ImageIcon, Languages, FileCheck,
  Eye, X, ChevronLeft, Lock, ArrowUpCircle, Ban, Phone, Bell, HeartPulse, ListChecks, ShieldCheck, Megaphone, Briefcase, Plug
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { useUsageTracking } from '../../hooks/useUsageTracking'; // Sprint 10: Real-time tracking
import { Toaster } from '../../components/Toaster';
import AddLeadModal from './admin/AddLeadModal';
// P0.1 FIX: Removed UsageStatsCard import - was using hardcoded PackageEnforcementContext data
// Also removed LimitWarningBanner - limits now come from SubscriptionContext
import { LockedFeatureBadge, UpgradeCTA, FeatureGate } from '../../components/package/PackageEnforcementUI';
import { SuspensionBanner, SuspensionReasonCard, DisabledSuspendedButton } from '../../components/suspension/SuspensionUI';
import { LeadFunnel } from '../../components/LeadFunnel'; // Sprint 9: Lead Funnel
import { ProviderPerformancePanel } from '../../components/ProviderPerformancePanel'; // Analytics sprint
import { ConversionTrendPanel } from '../../components/ConversionTrendPanel'; // Timeseries sprint
import { AnalyticsFilterBar } from '../../components/AnalyticsFilterBar'; // Lead ops sprint
import { CollapsibleAnalytics } from '../../components/CollapsibleAnalytics'; // Collapsible analytics sprint
import { useLayoutPreset } from '../../components/LayoutPresetSelector'; // Layout preset selector retired from dashboard (DASHBOARD-WIDGET-FLOW-01); hook kept for Command Palette compatibility
import { CommandPalette } from '../../components/CommandPalette'; // Command palette sprint
import { NavBadge, computeBadgeCounts } from '../../components/NavBadge'; // Nav badges sprint
import { SubscriptionBanner } from '../../components/subscription'; // Subscription monetization banner

// Sub-components for different sections
import LeadManagement from './admin/LeadManagement';
import LeadDistribution from './admin/LeadDistribution';
// TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02: embed the existing Aftercare
// dashboard at the bottom of the Provider Admin overview. The component
// at /clinic/aftercare is reused as the single source of truth — no
// duplicate logic, no new page, no new route.
import AfterSalesDashboard from './AfterSalesDashboard';
// NOTE (Step 1 — Navigation Cleanup): the inline profile tab surface has
// been removed. The dashboard's "Profile" nav button now navigates to
// /#/clinic/profile (the SSOT Provider Profile page). The legacy component
// at ./admin/ClinicProfileEditor.jsx was deleted alongside this change.
import TreatmentManagement from './admin/TreatmentManagement';
import DoctorManagement from './admin/DoctorManagement';
import SalesRepManagement from './admin/SalesRepManagement';
import DocumentManagement from './admin/DocumentManagement';
import ClinicQuotaStrip, { useQuotaSummary, isQuotaExceeded, handleQuotaError } from '../../components/ClinicQuotaStrip';
import OperationalWidgets from '../../components/OperationalWidgets';
// COA-10 G5 — clinic-wide post-acceptance summary on Admin Overview
import AdminOperationalSummaryCard from './AdminOperationalSummaryCard';
import api from '../../utils/api';
import MetaConnectionCard from '../../components/MetaConnectionCard';
import { PaymentAccountOnboardingCard } from './ClinicProfileEditor';
import { SettlementAccountsCard } from '../../components/clinic/SettlementAccountsCard';
import { PayTRIntegrationCard } from '../../components/clinic/PayTRIntegrationCard';
import { isMarketplaceLead, isProviderOwnedLead } from '../../utils/leadOrigin'; // TREATX-LEAD-ORIGIN-DISPLAY-01

// Sprint 10: Main dashboard content component
const ClinicAdminDashboardContent = ({ 
  subscription, 
  trialStatus, 
  canUseFeature, 
  packageSnapshot, 
  usage = {},
  checkUsageLimit,
  checkSubscriptionAction,  // Renamed from canPerformAction to avoid conflict
  onShowTrialWarning,
  onLimitBlocked,
  onUpgrade,
  // Sprint 10: Real-time tracking props
  realTimeUsage = null,
  onRefreshUsage = null,
  isRefreshingUsage = false,
}) => {
  const { t, i18n } = useTranslation(); // SPRINT 9: i18n hook
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    clinicUser, 
    logoutClinic, 
    getClinicStats,
    isClinicAdmin,
  } = useClinicData();
  const { clinicId } = useClinicScope();
  // PROVIDER-LOGOUT-ISOLATION-01 — canonical logout dependencies.
  const { clearRBAC } = useRBAC();
  const { logout } = useAuth();

  // API-driven sales rep data (Phase 2A: replaces static imports)
  const { salesReps: apiSalesReps, fetchSalesReps } = useSalesReps(clinicId);

  // Doctor affiliation count + data (SSOT: from provider_affiliations via enriched endpoint)
  const [affiliationCount, setAffiliationCount] = useState({ active: 0, pending: 0, total: 0 });
  const [affiliatedDoctors, setAffiliatedDoctors] = useState([]);
  const fetchDoctorAffiliations = React.useCallback(async () => {
    if (!clinicId) return;
    try {
      const { data } = await api.get(`/providers/${clinicId}/doctors`, {
      });
      if (data.success) {
        const affs = data.affiliations || [];
        setAffiliationCount({
          active: affs.filter(a => a.status === 'active').length,
          pending: affs.filter(a => a.status === 'pending').length,
          total: affs.length,
        });
        setAffiliatedDoctors(affs.filter(a => a.status === 'active').map(a => ({
          doctor_id: a.doctor_profile?.doctor_id || a.doctor_id || a.person_id,
          full_name: a.doctor_profile?.full_name || a.person_name || '',
          specialties: a.doctor_profile?.specialties || [],
          verification_status: a.doctor_profile?.verification_status || 'unverified',
        })));
      }
    } catch (err) {
      console.error('[Dashboard] fetch doctor affiliations:', err);
    }
  }, [clinicId]);

  // API-driven lead data (replaces getLeadsForClinic)
  const { leads: apiLeads, total: leadTotal, fetchLeads: fetchApiLeads, createLead: apiCreateLead } = useClinicLeads(clinicId);

  // Sprint 10: Default canUseFeature if not provided (for non-subscription flow)
  const checkFeature = canUseFeature || (() => false);
  const currentPackage = packageSnapshot || null;

  // Helper: extract patient display name from a lead (backend returns snake_case)
  const getPatientForLead = (lead) => {
    return {
      name: lead?.patient_name || 'Unknown',
      email: lead?.patient_email || '',
      phone: lead?.patient_phone || '',
      country: lead?.country_of_origin || 'Unknown',
    };
  };

  // P2-1: Package Enforcement
  // NOTE: We only use canAddUser, canReceiveMarketplaceLead, isFeatureAvailable from PackageEnforcement
  // Usage stats MUST come from SubscriptionContext (subscription.packageSnapshot.limits)
  // See P0.1 fix: Removed getUsageStats to prevent hardcoded Silver package data from being used
  const { 
    canAddUser, 
    canReceiveMarketplaceLead, 
    isFeatureAvailable
  } = usePackageEnforcement();

  // P2-2: Suspension Enforcement
  const {
    isClinicSuspended,
    getSuspensionDetails,
    canPerformAction
  } = useSuspensionEnforcement();

  const [activeTab, setActiveTab] = useState('overview');
  const [integrationProvider, setIntegrationProvider] = useState('meta');

  // UAT-MSG-VIEW-LEAD-02 — `/clinic/admin/leads/:leadId` previously rendered
  // this dashboard and DROPPED the id, so "View lead" from Messages landed on
  // an overview instead of the patient's lead. Open the Leads workspace on
  // that exact lead (LeadManagement already owns the lead detail).
  const routeLeadId = useParams().leadId;
  useEffect(() => {
    if (routeLeadId) setActiveTab('leads');
  }, [routeLeadId]);

  // O82 — deep link back from the Provider Profile editor:
  // /clinic/admin?tab=integrations&provider=iyzico reopens the exact panel the
  // provider left, so completing missing information is a round trip.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const provider = params.get('provider');
    if (tab) setActiveTab(tab);
    if (provider === 'meta' || provider === 'iyzico') setIntegrationProvider(provider);
  }, [location.search]);
  // OPS1: independent toggle for the secondary-nav row so operational
  // surfaces (aftercare, distribution, doctors, users, documents) become
  // discoverable without requiring a precondition activeTab.
  const [showMoreNav, setShowMoreNav] = useState(false);
  // TREATX-PROVIDER-ADMIN-NAVIGATION-CONSOLIDATION-01:
  // Tracks which Level 1 menu currently has its sub-menu panel open.
  // Holds the parent nav id (e.g. 'treatments') or null when closed.
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const submenuContainerRef = useRef(null);

  useEffect(() => {
    if (!openSubmenu) return undefined;
    const handleClickOutside = (event) => {
      if (submenuContainerRef.current && !submenuContainerRef.current.contains(event.target)) {
        setOpenSubmenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openSubmenu]);
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [widgetRefreshKey, setWidgetRefreshKey] = useState(0);

  // CLEANUP-02: Unified Operational Summary — collapse state persisted in localStorage
  // (key: clinic-admin.opsSummary.collapsed). `summaryRefreshKey` drives a synchronous
  // re-fetch of all three KPI groups when the single Refresh button is pressed.
  const OPS_SUMMARY_LS_KEY = 'clinic-admin.opsSummary.collapsed';
  const [summaryCollapsed, setSummaryCollapsed] = useState(() => {
    try { return window.localStorage.getItem(OPS_SUMMARY_LS_KEY) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(OPS_SUMMARY_LS_KEY, summaryCollapsed ? '1' : '0'); }
    catch { /* ignore quota / privacy mode */ }
  }, [summaryCollapsed]);
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);
  const [summaryRefreshing, setSummaryRefreshing] = useState(false);

  // TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02:
  // Collapse state for the grouped "New Leads + Sales Team Performance"
  // section. Persisted to localStorage; default expanded.
  const NEW_LEADS_SALES_LS_KEY = 'clinic-admin.newLeadsSalesPerf.collapsed';
  const [newLeadsSalesCollapsed, setNewLeadsSalesCollapsed] = useState(() => {
    try { return window.localStorage.getItem(NEW_LEADS_SALES_LS_KEY) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(NEW_LEADS_SALES_LS_KEY, newLeadsSalesCollapsed ? '1' : '0'); }
    catch { /* ignore quota / privacy mode */ }
  }, [newLeadsSalesCollapsed]);

  // TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02:
  // Collapse state + key-bump refresh for the embedded Aftercare
  // Overview (reuses AfterSalesDashboard verbatim, no rewrite). The
  // refresh key remounts the embedded component to force a re-fetch.
  const AFTERCARE_EMBED_LS_KEY = 'clinic-admin.aftercareEmbed.collapsed';
  const [aftercareEmbedCollapsed, setAftercareEmbedCollapsed] = useState(() => {
    try { return window.localStorage.getItem(AFTERCARE_EMBED_LS_KEY) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(AFTERCARE_EMBED_LS_KEY, aftercareEmbedCollapsed ? '1' : '0'); }
    catch { /* ignore quota / privacy mode */ }
  }, [aftercareEmbedCollapsed]);
  const [aftercareEmbedRefreshKey, setAftercareEmbedRefreshKey] = useState(0);
  
  // Sprint 7 Batch 3: Sorting state for offers table
  const [offerSort, setOfferSort] = useState({ column: 'createdAt', direction: 'desc' });

  // BATCH B1 — Offers Overview usability: scope (Latest/History), search, filters.
  // Presentation-only; canonical Offer/Quote authority (is_latest, status, version) is untouched.
  const [offerScope, setOfferScope] = useState('latest'); // 'all' | 'latest' | 'history'
  const [offerSearch, setOfferSearch] = useState('');
  const [offerFilters, setOfferFilters] = useState({ status: 'ALL', salesRep: 'ALL', treatment: 'ALL', dateFrom: '', dateTo: '' });

  // Lead Ops Sprint: Analytics filter state
  const [analyticsFilters, setAnalyticsFilters] = useState({ source: null, assigned_to: null, status: null });

  // Collapsible Analytics Sprint: Summary data for collapsed strip
  const [analyticsSummary, setAnalyticsSummary] = useState({
    conversionRate: undefined, conversionDelta: null,
    totalLeads: undefined, wonLeads: undefined, lostLeads: undefined,
  });
  // M11-CLOSURE-001 (2026-05-31) — lead-level numbers in analyticsSummary
  // (totalLeads / wonLeads / lostLeads / conversionRate) come from the
  // canonical /api/clinic/leads/summary endpoint which reads the `leads`
  // collection. Previously these read `message_threads.lead_status` via
  // analytics_service which caused M11 drift vs LeadManagement page.
  // The performance / trend widgets still call onSummaryUpdate but their
  // lead-counter writes are overwritten by this canonical fetch.
  const perfSummaryUpdate = useCallback((s) => setAnalyticsSummary(prev => ({ ...prev, ...s })), []);
  const trendSummaryUpdate = useCallback((s) => setAnalyticsSummary(prev => ({ ...prev, ...s })), []);
  useEffect(() => {
    if (!clinicId) return undefined;
    let cancelled = false;
    const fetchLeadsCanonical = async () => {
      try {
        const res = await api.get('/clinic/leads/summary');
        if (cancelled) return;
        const b = res.data?.buckets || {};
        const cr = res.data?.conversion_rate;
        setAnalyticsSummary(prev => ({
          ...prev,
          totalLeads:     b.all ?? prev.totalLeads,
          wonLeads:       b.won ?? prev.wonLeads,
          lostLeads:      b.lost ?? prev.lostLeads,
          conversionRate: cr ?? prev.conversionRate,
        }));
      } catch (_err) { /* silent */ }
    };
    fetchLeadsCanonical();
    const tid = setInterval(fetchLeadsCanonical, 60_000);
    return () => { cancelled = true; clearInterval(tid); };
  }, [clinicId]);

  // TREATX-PROVIDER-ADMIN-DASHBOARD-WIDGET-FLOW-01:
  // The Operations / Analytics / Executive visual category switcher has
  // been retired. All dashboard widgets are now always visible in a
  // continuous workflow-oriented vertical layout. The `useLayoutPreset`
  // hook is kept so the global Ctrl+K Command Palette commands that
  // toggle preset state continue to function without runtime errors —
  // but the dashboard visibility no longer depends on the preset value.
  const [layoutPreset, setLayoutPreset] = useLayoutPreset();
  const presetVis = {
    analyticsExpanded: true,
    analyticsVisible:  true,
    showTable:         true,
    showSourceCards:   true,
    showQuickActions:  true,
  };

  // Nav Badges Sprint: Cached threads + badge counts
  const [cachedThreads, setCachedThreads] = useState([]);
  // M11-CLOSURE-001 (2026-05-31) — badge counts now come from the
  // canonical /api/clinic/nav-badges/summary endpoint which reads the
  // `leads` collection for lead-level badges (sla_breach, unassigned,
  // new_quote_requests) and `message_threads` ONLY for message-domain
  // badges (escalated, guard_risk). Previously slaBreach/unassigned
  // were computed client-side from threads which caused M11 drift.
  const [badgeCounts, setBadgeCounts] = useState({ escalated: 0, guardRisk: 0, slaBreach: 0, unassigned: 0 });

  // QRI-001 (2026-05-28) — Quote inbox "NEW" counter.
  // PERF-AUDIT-2026-06: the count is read from the SAME canonical counter
  // authority the other nav badges already use
  // (`GET /api/clinic/nav-badges/summary` → `badges.leads.new_quote_requests`,
  // which counts leads with source=QUOTE_REQUEST + status=NEW for this
  // clinic — identical semantics to the inbox NEW bucket). The dashboard no
  // longer downloads the full `/api/clinic/quote-requests` inbox payload
  // (47 KB, measured as the slowest call on this surface) just to derive one
  // integer. Still ONE source of truth, still refreshed on the same 60 s
  // cadence (set by the nav-badges poller below).
  const [quoteInboxNewCount, setQuoteInboxNewCount] = useState(0);

  useEffect(() => {
    if (!clinicId) return;
    let cancelled = false;
    const fetchNavBadges = async () => {
      try {
        const res = await api.get('/clinic/nav-badges/summary');
        if (cancelled) return;
        const b = res.data?.badges || {};
        setBadgeCounts({
          escalated:  b.messages?.escalated || 0,
          guardRisk:  b.messages?.guard_risk || 0,
          slaBreach:  b.leads?.sla_breach || 0,
          unassigned: b.leads?.unassigned || 0,
        });
        // QRI-001 badge — same canonical counter authority (see above).
        setQuoteInboxNewCount(b.leads?.new_quote_requests || 0);
      } catch (_err) { /* silent — badges stay at last good value on network fail */ }
    };
    // Initial fetch
    fetchNavBadges();
    // Refresh every 60s (matches the QRI-001 cadence for the QR badge)
    const tid = setInterval(fetchNavBadges, 60_000);
    // PERF-AUDIT-2026-06: the eager `/v2/threads/clinic/{id}?limit=200`
    // preload (57 KB on every dashboard mount) was removed. Its ONLY
    // consumer is the Ctrl+K CommandPalette, which already fetches the
    // same list itself the first time it is opened — so the data is
    // identical and nothing is fetched until the user actually needs it.
    return () => { cancelled = true; clearInterval(tid); };
  }, [clinicId, isClinicAdmin, clinicUser?.id, clinicUser?.userId]);
  
  // P2-1: Package usage stats
  // REMOVED: packageStats - was using hardcoded data from PackageEnforcementContext
  // Usage stats now come ONLY from SubscriptionContext (subscription.packageSnapshot.limits)
  const [userLimitCheck, setUserLimitCheck] = useState({ allowed: true });
  const [leadLimitCheck, setLeadLimitCheck] = useState({ allowed: true });

  // Fetch offers for the clinic (Offers tab)
  // PERF-AUDIT-2026-06: this is a 208 KB payload whose ONLY consumers are
  // rendered inside `activeTab === 'offers'` (offer counts, totals, filters
  // and the offers table). It is therefore fetched when that tab is opened
  // instead of on every dashboard mount. Once loaded it is kept in state,
  // so switching tabs back and forth does not refetch.
  const clinicCatalogLoadedRef = useRef(false);
  const offersLoadedRef = useRef(false);
  useEffect(() => {
    if (!clinicId) return;
    if (activeTab !== 'offers' || offersLoadedRef.current) return;
    offersLoadedRef.current = true;
    (async () => {
      try {
        const _t = '';
        const res = await api.get(`/clinics/${clinicId}/offers?limit=200`, {
          headers: {}
        });
        const data = res.data;
        // Normalize snake_case → camelCase for OfferCard compatibility
          const normalized = (data.offers || []).map(o => ({
            ...o,
            patientName: o.patient_name || o.patientName,
            treatmentName: o.treatment_name || o.treatmentName,
            // Canonical aggregate truth comes from backend financial_summary (compute_quote_totals).
            totalPrice: o.financial_summary?.total ?? 0,
            basePrice: o.financial_summary?.base_price ?? o.base_price,
            additionalServicesTotal: o.financial_summary?.additional_services_total ?? 0,
            balance: o.financial_summary?.balance,
            perInstallmentAmount: o.financial_summary?.per_installment_amount,
            createdAt: o.created_at || o.createdAt,
            sentAt: o.sent_at || o.created_at || o.sentAt,
            updatedAt: o.updated_at || o.updatedAt,
            validUntil: o.valid_until || o.validUntil,
            createdByName: o.agent_name || o.createdByName,
            createdBy: o.agent_id || o.createdBy,
            includedItems: o.included_items || o.includedItems || [],
            hotelNights: o.hotel_nights || o.hotelNights,
            hotelType: o.hotel_type || o.hotelType,
            airportTransfer: o.airport_transfer ?? o.airportTransfer,
            proposedDates: o.proposed_dates || o.proposedDates,
            deposit: o.deposit_amount || o.deposit || 0,
            depositType: o.deposit_type || o.depositType,
            depositValue: o.deposit_value || o.depositValue,
            paymentStructure: o.payment_structure || o.paymentStructure,
            installmentCount: o.installment_count || o.installmentCount,
            version: o.version_number || o.version,
            additionalServices: o.additional_services || o.additionalServices || [],
            terms: o.terms || '',
            notesPatientVisible: o.notes_patient_visible || o.notesPatientVisible,
            notesInternal: o.notes_internal || o.notesInternal,
            treatment: o.treatment_name || o.treatment || o.treatmentName || '',
          }));
          setOffers(normalized);
      } catch (err) { console.error('Offers fetch:', err); offersLoadedRef.current = false; }
    })();
  }, [clinicId, activeTab]);
  
  // P2-2: Suspension state
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionDetails, setSuspensionDetails] = useState(null);
  
  // Add loading state to prevent premature redirect
  const [isInitialized, setIsInitialized] = useState(false);
  // P0-FIX: Deterministic hydration timeout — if clinicUser doesn't hydrate in 4s, show error
  const [hydrationTimedOut, setHydrationTimedOut] = useState(false);

  // Q2: Quota summary for quota strip
  const { quota: quotaSummary, refetchQuota } = useQuotaSummary();

  // Initialize after first render to allow context to settle
  useEffect(() => {
    // Short delay to allow sessionStorage to be read
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // P0-FIX: Start hydration timeout when initialized but clinicUser missing
  // Increased to 8s to allow for slow initial logins on newly provisioned clinics
  useEffect(() => {
    if (!isInitialized || clinicUser) {
      setHydrationTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setHydrationTimedOut(true), 8000);
    return () => clearTimeout(timer);
  }, [isInitialized, clinicUser]);

  // Redirect if not authenticated (only after initialization)
  // NOTE: RBACProtectedRoute already guards this route.
  // Only redirect if ClinicDataContext is not loading and there's no RBAC auth.
  // This prevents bouncing when ClinicDataContext hasn't caught up yet.
  const { isAuthenticated } = useRBAC();
  useEffect(() => {
    if (isInitialized && !isAuthenticated && !clinicUser) {
      navigate('/login');
    }
  }, [isAuthenticated, clinicUser, navigate, isInitialized]);

  // Load data on mount and when clinicUser changes
  useEffect(() => {
    if (clinicUser && isClinicAdmin && !isLoaded) {
      // Leads now come from useClinicLeads (API-driven)
      fetchApiLeads();
      setLeads(apiLeads);
      setStats(getClinicStats(clinicId));
      // Phase 2A: fetch from API instead of static data
      fetchSalesReps();
      fetchDoctorAffiliations();
      // PERF-AUDIT-2026-06: the clinic catalog projection
      // (`/public/catalog/clinics/{id}`) is consumed ONLY by the Treatments
      // tab (`clinic` prop). It was fetched here on every dashboard mount —
      // and duplicated by SuspensionEnforcementContext, which reads the same
      // endpoint. Moved to the Treatments-tab effect below.

      // P2-1: Load package enforcement data
      // P0.1 FIX: REMOVED hardcoded 'clinic-001' and getUsageStats() call
      // Usage stats now come ONLY from SubscriptionContext (subscription.packageSnapshot.limits)
      // Using actual clinicId for limit checks
      const providerId = clinicId;
      setUserLimitCheck(canAddUser(providerId, 'salesRep'));
      setLeadLimitCheck(canReceiveMarketplaceLead(providerId));
      
      // P2-2: Check suspension status
      const suspended = isClinicSuspended(clinicId);
      setIsSuspended(suspended);
      if (suspended) {
        setSuspensionDetails(getSuspensionDetails(clinicId));
      }
      
      setIsLoaded(true);
    }
  }, [clinicUser, isClinicAdmin, isLoaded, getClinicStats, canAddUser, canReceiveMarketplaceLead, isClinicSuspended, getSuspensionDetails, fetchApiLeads, fetchDoctorAffiliations, fetchSalesReps]);

  // Clinic context switch: force data reload when RBAC activeClinicId changes
  const prevClinicRef = useRef(clinicId);
  useEffect(() => {
    if (clinicId && prevClinicRef.current !== clinicId) {
      prevClinicRef.current = clinicId;
      setIsLoaded(false); // Reset isLoaded → triggers the data loading useEffect above
      // PERF-AUDIT-2026-06: tab-scoped payloads belong to the previous
      // clinic — allow them to reload for the new one.
      offersLoadedRef.current = false;
      clinicCatalogLoadedRef.current = false;
    }
  }, [clinicId]);

  // PERF-AUDIT-2026-06 — clinic catalog projection, loaded when the
  // Treatments tab (its only consumer) is opened. Same endpoint, same
  // `found`/`clinic` contract as before; just not on every mount.
  useEffect(() => {
    if (!clinicId || activeTab !== 'treatments' || clinicCatalogLoadedRef.current) return;
    clinicCatalogLoadedRef.current = true;
    (async () => {
      try {
        const res = await api.get(`/public/catalog/clinics/${clinicId}`);
        if (res.data?.found) setClinic(res.data.clinic);
      } catch (e) { clinicCatalogLoadedRef.current = false; }
    })();
  }, [clinicId, activeTab]);

  const loadData = () => {
    if (!clinicId) return;
    fetchApiLeads(); // Leads from API
    setStats(getClinicStats(clinicId));
    fetchSalesReps(); // Phase 2A: API-driven
    fetchDoctorAffiliations(); // SSOT: affiliation-based count
    // Fetch clinic from API catalog
    const API = process.env.REACT_APP_BACKEND_URL;
    api.get(`/public/catalog/clinics/${clinicId}`)
      .then(({ data }) => data)
      .then(data => { if (data.found) setClinic(data.clinic); })
      .catch(() => {});
  };

  // CLEANUP-02: Unified refresh — bumps both children's refreshKey and re-runs loadData.
  const handleRefreshSummary = () => {
    setSummaryRefreshing(true);
    try {
      loadData();
      setSummaryRefreshKey(k => k + 1);
      setWidgetRefreshKey(k => k + 1);
    } finally {
      setTimeout(() => setSummaryRefreshing(false), 600);
    }
  };

  // Sync API leads into local state for dashboard widgets
  useEffect(() => {
    if (apiLeads && apiLeads.length >= 0) {
      setLeads(apiLeads);
    }
  }, [apiLeads]);

  // Phase 2A: Sync API sales reps into local state
  useEffect(() => {
    if (apiSalesReps?.length >= 0) setSalesReps(apiSalesReps);
  }, [apiSalesReps]);

  // PROVIDER-LOGOUT-ISOLATION-01 — canonical logout.
  //
  // This handler previously called ONLY `logoutClinic()`, which clears a
  // single sessionStorage mirror. The HttpOnly auth cookie was never
  // expired (`/api/auth/unified/me` kept answering 200 for the signed-out
  // provider), and `treatx_unified_user` / RBAC permissions / workspaces /
  // last-active-workspace all survived — so authenticated navigation
  // stayed rendered and the next provider's session started contaminated.
  //
  // We now reuse the EXACT canonical sequence already used by the global
  // header (`components/Header.jsx`): no new session authority is
  // introduced. `AuthContext.logout()` is the only fire point for
  // `POST /api/auth/logout`.
  const handleLogout = () => {
    logoutClinic();
    clearRBAC();
    clearUnifiedAuth();
    if (logout) logout();
    navigate('/login');
  };

  // Sprint 7 Batch 3: Sort toggle handler for offers
  const toggleOfferSort = (column) => {
    setOfferSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // BATCH B1 — helpers reading ONLY canonical fields already on the offer.
  const offerRepName = (o) => o.agent_name || o.createdByName || '';
  const offerRepId = (o) => o.agent_id || o.createdBy || '';
  const offerTreatment = (o) => o.treatment_name || o.treatment || o.treatmentName || '';
  const offerCreatedTs = (o) => new Date(o.created_at || o.createdAt || 0).getTime();

  // BATCH B1 — filter option lists derived from the loaded dataset (no extra requests).
  const offerFilterOptions = React.useMemo(() => {
    const statuses = new Set(); const reps = new Map(); const treatments = new Set();
    (offers || []).forEach(o => {
      if (o.status) statuses.add(o.status);
      const rid = offerRepId(o);
      if (rid) reps.set(rid, offerRepName(o) || rid);
      const tr = offerTreatment(o);
      if (tr) treatments.add(tr);
    });
    return {
      statuses: Array.from(statuses).sort(),
      reps: Array.from(reps.entries()).sort((a, b) => a[1].localeCompare(b[1])),
      treatments: Array.from(treatments).sort((a, b) => a.localeCompare(b)),
    };
  }, [offers]);

  // OFFERS-UX-FINAL-CLOSURE — scope tri-state: All | Latest | History.
  //   All     → the complete loaded offer population (no is_latest/actionability/
  //             status/expiry/supersession exclusion). Existing filters/search/sort
  //             operate on this population.
  //   Latest  → current-version offers (is_latest !== false). UNCHANGED. Latest is
  //             version-recency, NOT lifecycle "Active".
  //   History → prior versions (is_latest === false). UNCHANGED.
  // Canonical Offer/Quote authority (is_latest, status, quote_actionable, supersession)
  // is NOT redefined here — this is a presentation scope only.
  const visibleOffers = React.useMemo(() => {
    let list = (offers || []).filter(o =>
      offerScope === 'all' ? true :
      offerScope === 'history' ? o.is_latest === false : o.is_latest !== false
    );

    const q = offerSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(o =>
        (o.patient_name || o.patientName || '').toLowerCase().includes(q) ||
        offerTreatment(o).toLowerCase().includes(q) ||
        offerRepName(o).toLowerCase().includes(q)
      );
    }

    const { status, salesRep, treatment, dateFrom, dateTo } = offerFilters;
    if (status !== 'ALL') list = list.filter(o => o.status === status);
    if (salesRep !== 'ALL') list = list.filter(o => offerRepId(o) === salesRep);
    if (treatment !== 'ALL') list = list.filter(o => offerTreatment(o) === treatment);
    if (dateFrom) { const f = new Date(dateFrom).getTime(); list = list.filter(o => offerCreatedTs(o) >= f); }
    if (dateTo) { const tEnd = new Date(dateTo).getTime() + 86400000 - 1; list = list.filter(o => offerCreatedTs(o) <= tEnd); }

    const dir = offerSort.direction === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      let comparison = 0;
      switch (offerSort.column) {
        case 'totalPrice': {
          // Mixed-currency safe: group by currency (never compare EUR vs TRY as equal),
          // then order by amount within a currency. No FX conversion for sorting.
          const ca = (a.currency || 'EUR'), cb = (b.currency || 'EUR');
          if (ca !== cb) return ca.localeCompare(cb);
          comparison = (a.totalPrice || 0) - (b.totalPrice || 0);
          break;
        }
        case 'createdAt': comparison = offerCreatedTs(a) - offerCreatedTs(b); break;
        case 'patientName': comparison = (a.patient_name || a.patientName || '').localeCompare(b.patient_name || b.patientName || ''); break;
        case 'treatment': comparison = offerTreatment(a).localeCompare(offerTreatment(b)); break;
        case 'salesRep': comparison = offerRepName(a).localeCompare(offerRepName(b)); break;
        case 'status': comparison = (a.status || '').localeCompare(b.status || ''); break;
        default: comparison = 0;
      }
      return comparison * dir;
    });
  }, [offers, offerScope, offerSearch, offerFilters, offerSort]);

  const allCount = React.useMemo(() => (offers || []).length, [offers]);
  const latestCount = React.useMemo(() => (offers || []).filter(o => o.is_latest !== false).length, [offers]);
  const historyCount = React.useMemo(() => (offers || []).filter(o => o.is_latest === false).length, [offers]);
  const offerFiltersActive = offerSearch.trim() !== '' || offerFilters.status !== 'ALL' || offerFilters.salesRep !== 'ALL' || offerFilters.treatment !== 'ALL' || offerFilters.dateFrom !== '' || offerFilters.dateTo !== '';
  const resetOfferFilters = () => { setOfferSearch(''); setOfferFilters({ status: 'ALL', salesRep: 'ALL', treatment: 'ALL', dateFrom: '', dateTo: '' }); };

  // DASHBOARD-CONSISTENCY-C1 — single human sales funnel for ALL dashboard sales widgets,
  // read from the backend `human_stage` projection (same as Lead Management / Sales Rep).
  // Total = New + Working + On Hold + Won + Lost (reconciles by construction).
  const funnelCounts = React.useMemo(() => {
    const c = { NEW: 0, WORKING: 0, ON_HOLD: 0, WON: 0, LOST: 0 };
    (leads || []).forEach(l => {
      const hs = l.status_transitions?.human_stage;
      if (hs && c.hasOwnProperty(hs)) c[hs]++;
    });
    return { ...c, total: (leads || []).length };
  }, [leads]);

  // DASHBOARD-CONSISTENCY-C1 — ONE source truth for the dashboard: source counts derive
  // from the same `leads` population the Lead Funnel uses (was reading `sourceStats`, a
  // state that was never populated → always 0). Reconciles with total + funnel.
  const sourceCounts = React.useMemo(() => {
    const mk = (leads || []).filter(isMarketplaceLead);
    const po = (leads || []).filter(isProviderOwnedLead);
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const newSince = (arr) => arr.filter(l => new Date(l.created_at || l.createdAt || 0).getTime() >= weekAgo).length;
    return {
      marketplace: { total: mk.length, new: newSince(mk) },
      providerOwned: { total: po.length, new: newSince(po) },
    };
  }, [leads]);

  // DASHBOARD-CONSISTENCY-C1 — Plan Usage reads the real subscription authority
  // (GET /api/clinics/{id}/usage via SubscriptionContext) but the backend returns snake_case
  // keys inside a `{ usage: {...} }` envelope, while the card expects leads/salesReps/
  // offersThisMonth. Map to the card's shape here (local reader fix; no subscription
  // architecture change, no fabricated counts).
  const planUsage = React.useMemo(() => {
    const src = (realTimeUsage && realTimeUsage.usage) || realTimeUsage || (usage && usage.usage) || usage || {};
    return {
      leads: src.leads_per_month ?? src.leads ?? 0,
      salesReps: src.sales_reps ?? src.salesReps ?? 0,
      offersThisMonth: src.offers_per_month ?? src.offersThisMonth ?? 0,
    };
  }, [usage, realTimeUsage]);

  // B (Launch UX) — Offers Overview "Total Value" must NOT add different currencies as one
  // number, nor inflate value by summing historical versions. Sum by currency over the
  // canonically patient-actionable offers ONLY (backend `quote_actionable`). No FX.
  const latestOfferTotals = React.useMemo(() => {
    const m = {};
    (offers || []).forEach(o => {
      // Canonical commercial truth from the backend offer projection: quote_actionable is
      // true only for live, still-acceptable offers (SENT/VIEWED on a commercially-open lead)
      // and false for superseded / rejected / expired / closed / ACCEPTED offers. Accepted
      // (won) value belongs to downstream order/financial reporting — TreatX rule: ACCEPTED ≠ WON.
      if (!o.quote_actionable) return;
      const cur = o.currency || 'EUR';
      m[cur] = (m[cur] || 0) + (o.totalPrice || 0);
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [offers]);


  // Sprint 7 Batch 3: Render sort indicator inline
  const renderSortIndicator = (column) => {
    if (offerSort.column !== column) {
      return <span className="text-gray-300 ml-1">↕</span>;
    }
    return (
      <span className="text-blue-600 ml-1">
        {offerSort.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // RBAC-RESILIENT RENDERING: Never return null. Use RBAC auth as primary gate.
  // clinicUser from sessionStorage is optional enhancement, not a render requirement.
  if (!isAuthenticated) {
    // Truly not logged in — redirect handled by useEffect above
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="clinic-admin-redirect">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">{t('provider.adminDash.redirectingToLogin')}</p>
        </div>
      </div>
    );
  }
  if (!clinicUser) {
    // P0-FIX: Deterministic loading with 4s timeout → show error + retry
    if (hydrationTimedOut) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="clinic-admin-error">
          <div className="text-center max-w-md">
            <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-800 mb-2">{t('provider.adminDash.dashboardCouldNotLoad')}</h2>
            <p className="text-sm text-slate-500 mb-4">{t('provider.adminDash.profileCouldNotLoad')}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" size="sm" onClick={() => window.location.reload()} data-testid="retry-clinic-load-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> {t('common.retry')}
              </Button>
              <Button variant="default" size="sm" onClick={() => { 
                localStorage.clear(); sessionStorage.clear(); window.location.hash = '#/login';
              }} data-testid="relogin-btn">
                {t('common.relogin')}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="clinic-admin-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">{t('provider.adminDash.loadingClinicDashboard')}</p>
        </div>
      </div>
    );
  }

  // P3-1: Simplified navigation - grouped by primary actions
  // TREATX-PROVIDER-ADMIN-NAVIGATION-CONSOLIDATION-01:
  // Level 1 parents now expose `submenu` arrays so previously-buried
  // child pages are discoverable directly under their logical parent
  // (without changing routes or adding new pages). The "More" dropdown
  // is retired once `secondaryNavItems` is empty.
  const navItems = [
    { id: 'overview', labelKey: 'adminNav.dashboard', icon: LayoutDashboard, primary: true, badges: [
      { count: badgeCounts.slaBreach, color: 'amber', testId: 'badge-sla' },
      { count: badgeCounts.unassigned, color: 'grey', testId: 'badge-unassigned' },
    ]},
    { id: 'leads', labelKey: 'adminNav.leads', icon: Users, primary: true, badges: [
      { count: badgeCounts.slaBreach, color: 'amber', testId: 'badge-leads-sla' },
    ], submenu: [
      // TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02: Lead Distribution
      // relocated from Sales Team into Leads — it operates on leads, not
      // on the sales team itself. Route/tab unchanged (`distribution`).
      { id: 'distribution', labelKey: 'adminNav.leadDistribution', icon: Zap },
    ]},
    // WORKFLOW-OPS-001-PHASE-1 (P0-A): primary "Cases" tab so the
    // canonical operational case list is reachable in 1 click from the
    // admin dashboard. Routes to /clinic/cases — no parallel authority.
    { id: 'cases', label: 'Cases', icon: Briefcase, primary: true, href: '/clinic/cases' },
    { id: 'offers', labelKey: 'adminNav.offers', icon: DollarSign, primary: false, submenu: [
      { id: 'campaigns', label: 'Campaigns', icon: Megaphone, href: '/clinic/campaigns' },
      { id: 'campaign-alerts', labelKey: 'adminNav.campaignAlerts', icon: Bell, href: '/clinic/notifications' },
    ]},
    { id: 'treatments', labelKey: 'adminNav.treatments', icon: FileText, primary: false, submenu: [
      { id: 'milestone-templates', label: 'Treatment Plans', icon: ListChecks, href: '/clinic/treatment-plans' },
      { id: 'before-after', label: 'Before/After', icon: ShieldCheck, href: '/clinic/before-after-cases' },
    ]},
    { id: 'sales-reps', labelKey: 'adminNav.salesTeam', icon: UserCog, primary: false, submenu: [
      // TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02: Lead Distribution
      // moved out of Sales Team into Leads. Sales Team now contains only
      // user management and doctor management.
      { id: 'users', labelKey: 'adminNav.userManagement', icon: UserPlus },
      { id: 'doctors', labelKey: 'adminNav.doctors', icon: Stethoscope },
    ]},
    // TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02: Aftercare removed from
    // Level 2 navigation. The existing Aftercare dashboard
    // (`AfterSalesDashboard`) is embedded at the bottom of the
    // overview tab instead. The legacy route `/clinic/aftercare`
    // continues to render the same component (unchanged).
    { id: 'profile', label: 'Profile', href: '/clinic/profile', icon: Settings, primary: true, submenu: [
      // TREATX-UAT-D4-01 (2026-08): restore the discoverable submenu
      // entry for the canonical Provider Company / Legal Profile page.
      // Deleting the inline `admin/ClinicProfileEditor.jsx` in April
      // moved the SSOT to `/clinic/profile` (canonical
      // `pages/clinic/ClinicProfileEditor.jsx`), but the later Profile
      // submenu addition did not include a link back to that page —
      // so opening the Profile dropdown appeared to hide the Company /
      // Legal identity + banking surface entirely. Clicking the parent
      // label still navigates there, but the submenu now surfaces it
      // explicitly (this entry only — no new page, no new route, no
      // parallel profile / banking / legal-entity store).
      { id: 'company-legal', label: 'Company & Legal', icon: Settings, href: '/clinic/profile' },
      { id: 'clinic-logo', label: 'Clinic Logo', icon: ImageIcon, href: '/clinic/admin/logo' },
      { id: 'facility-media', label: 'Facility Photos', icon: ImageIcon, href: '/clinic/admin/facility-media' },
      { id: 'documents', labelKey: 'adminNav.documents', icon: FileCheck },
      // TREATX-PROVIDER-CMS-CONTRACTS-01 — canonical legal_documents UI.
      { id: 'contracts', label: 'Contracts', icon: ShieldCheck, href: '/clinic/admin/contracts' },
      // TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02: Notifications relocated
      // from Aftercare submenu (which no longer exists at Level 2) into
      // Profile. Route unchanged: `/clinic/settings/notifications`.
      { id: 'notifications', labelKey: 'adminNav.notifications', icon: Bell, href: '/clinic/settings/notifications' },
      // TREATX-PROVIDER-META-IA-RELOCATION-03: Provider Meta / WhatsApp
      // connection management relocated out of the operational Lead
      // Distribution tab into Settings → Integrations. activeTab-based
      // (id `integrations`); no new route, no new connection authority.
      { id: 'integrations', labelKey: 'settings.sections.integrations', icon: Plug },
    ]},
  ];

  const messagesNavBadgeTotal = badgeCounts.escalated + badgeCounts.guardRisk;
  
  // P3-1: Secondary navigation items (accessible via the "More" toggle).
  // TREATX-PROVIDER-ADMIN-NAVIGATION-CONSOLIDATION-01: all previously
  // listed entries (lead distribution, doctors, user management,
  // documents, treatment plans, before/after, campaigns, clinic logo,
  // facility photos, campaign alerts, notifications) have been moved
  // under their respective Level 1 parents. The list is now empty, so
  // the "More" toggle hides entirely below.
  const secondaryNavItems = [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      
      {/* P2-3: Build stamp for deployment verification */}
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-green-600 text-white text-center py-1.5 font-bold text-sm shadow-lg">
      </div>
      
      {/* P2-2: Suspension Banner - Shows when account is suspended */}
      {isSuspended && (
        <div className="fixed top-12 left-0 right-0 z-[9998]">
          <SuspensionBanner 
            suspensionDetails={suspensionDetails}
            onContactSupport={() => window.location.href = 'mailto:support@treatx.health'}
          />
        </div>
      )}
      
      {/* P3-1: Header focused on "How is my clinic performing & what is blocked?" */}
      <header className={`bg-slate-900 text-white sticky ${isSuspended ? 'top-[7.5rem]' : 'top-12'} z-40`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold" data-testid="clinic-admin-title">{t('adminDashboard.title')}</h1>
                <p className="text-sm text-slate-300">{clinicUser.clinicName}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Sprint 10: Trial/Subscription Badge */}
              {subscription && (
                <TrialBadge 
                  subscription={subscription}
                  trialStatus={trialStatus}
                  size="default"
                  onShowWarning={onShowTrialWarning}
                />
              )}
              <Badge variant="outline" className="bg-purple-500/20 text-purple-200 border-purple-400/30">
                <Award className="h-3 w-3 mr-1" />
                {t('common.admin')}
              </Badge>
              <div className="flex items-center gap-2">
                {clinicUser.avatar && (
                  <img src={clinicUser.avatar} alt="" className="w-8 h-8 rounded-full" />
                )}
                <span className="text-sm">{clinicUser.name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-300 hover:text-white hover:bg-slate-800" data-testid="admin-logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Command Palette (Ctrl+K) */}
      <CommandPalette
        isAdmin={isClinicAdmin}
        userId={clinicUser?.id || clinicUser?.userId}
        clinicId={clinicId}
        onPresetChange={setLayoutPreset}
        onTabChange={setActiveTab}
        analyticsSummary={analyticsSummary}
        preloadedThreads={cachedThreads}
      />

      {/* P3-1: Simplified Navigation with primary/secondary separation */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2">
            {/* Primary Navigation */}
            {/* TREATX-PROVIDER-ADMIN-NAVIGATION-CONSOLIDATION-01:
                `overflow-x-clip` keeps horizontal clipping behaviour while
                allowing the submenu dropdown panels to overflow vertically
                outside the nav row. `overflow-x-auto` would force
                `overflow-y: auto` and clip the dropdowns. */}
            <div className="flex space-x-1 overflow-x-clip" ref={submenuContainerRef}>
              {navItems.map(item => {
                const hasSubmenu = Array.isArray(item.submenu) && item.submenu.length > 0;
                const isOpen = openSubmenu === item.id;
                const isActiveSubmenuChild = hasSubmenu && item.submenu.some(c => c.id === activeTab);
                return (
                  <div key={item.id} className="relative">
                    <div className="flex items-stretch">
                      <button
                        onClick={() => item.href ? navigate(item.href) : setActiveTab(item.id)}
                        data-testid={`nav-${item.id}`}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                          (!item.href && activeTab === item.id) || isActiveSubmenuChild
                            ? 'bg-slate-900 text-white'
                            : item.primary
                              ? 'text-slate-800 hover:bg-slate-100 font-semibold'
                              : 'text-slate-600 hover:bg-slate-100'
                        } ${hasSubmenu ? 'pr-1 rounded-r-none' : ''}`}
                      >
                        <item.icon className="h-4 w-4" />
                        {item.label || t(item.labelKey)}
                        {item.badges?.map(b => (
                          <NavBadge key={b.testId} count={b.count} color={b.color} testId={b.testId} />
                        ))}
                      </button>
                      {hasSubmenu && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setOpenSubmenu(isOpen ? null : item.id); }}
                          data-testid={`nav-${item.id}-caret`}
                          aria-expanded={isOpen}
                          aria-haspopup="menu"
                          aria-label={`${item.label || t(item.labelKey)} submenu`}
                          className={`flex items-center px-1.5 py-2 rounded-lg rounded-l-none text-sm transition-colors ${
                            (!item.href && activeTab === item.id) || isActiveSubmenuChild
                              ? 'bg-slate-900 text-white'
                              : 'text-slate-500 hover:bg-slate-100'
                          }`}
                        >
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    {hasSubmenu && isOpen && (
                      <div
                        role="menu"
                        data-testid={`nav-${item.id}-submenu`}
                        className="absolute left-0 top-full mt-1 min-w-[200px] bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50"
                      >
                        {item.submenu.map(child => (
                          <button
                            key={child.id}
                            role="menuitem"
                            onClick={() => {
                              setOpenSubmenu(null);
                              if (child.href) navigate(child.href);
                              else setActiveTab(child.id);
                            }}
                            data-testid={`nav-submenu-${child.id}`}
                            className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap transition-colors ${
                              activeTab === child.id
                                ? 'bg-slate-100 text-slate-900 font-medium'
                                : 'text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <child.icon className="h-4 w-4 text-slate-500" />
                            {child.label || t(child.labelKey)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02:
                  Messages removed from Level 2 nav (it is already in the
                  Level 1 global nav). The escalated + guard badges that
                  this button surfaced continue to be visible from the
                  Level 1 Messages entry. Route /clinic/messages is
                  unchanged and remains reachable. */}
              {/* Quote Requests (Q3) — QRI-001: NEW-count badge from
                  the SAME `/api/clinic/quote-requests` source the inbox
                  reads, so badge ↔ inbox-NEW-bucket cannot drift. */}
              <button
                onClick={() => navigate('/clinic/quote-requests')}
                data-testid="nav-quote-requests"
                data-render-authority="QuoteInboxNavBadge-v1"
                data-render-generation="QRI-001"
                data-sprint-fingerprint="COM-FOUNDATION"
                className="relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors text-slate-800 hover:bg-slate-100"
              >
                <FileText className="h-4 w-4" />
                Quotes
                {quoteInboxNewCount > 0 && (
                  <span
                    data-testid="nav-quote-requests-new-badge"
                    className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold leading-none"
                    aria-label={`${quoteInboxNewCount} new quote requests`}
                    title={`${quoteInboxNewCount} new quote requests`}
                  >
                    {quoteInboxNewCount > 99 ? '99+' : quoteInboxNewCount}
                  </span>
                )}
              </button>
            </div>
            {/* Secondary Navigation Dropdown */}
            {/* TREATX-PROVIDER-ADMIN-NAVIGATION-CONSOLIDATION-01:
                The "More" toggle is suppressed when no secondary items
                remain. All previously-buried destinations now live under
                their logical Level 1 parents (Treatments, Profile, Sales
                Team, Offers, Aftercare). */}
            {secondaryNavItems.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowMoreNav(prev => !prev)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    (showMoreNav || ['distribution', 'doctors', 'users', 'documents'].includes(activeTab))
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-500 hover:bg-slate-100'
                  }`}
                  data-testid="nav-more"
                  aria-expanded={showMoreNav}
                >
                  {t('adminNav.more')}
                  <ChevronRight className={`h-4 w-4 transition-transform ${showMoreNav ? 'rotate-90' : ''}`} />
                </button>
              </div>
            )}
          </div>
          {/* OPS1: secondary tabs row — toggled by "More" button. Previously
              only rendered when `activeTab` was in a fixed allow-list,
              which made operational sub-surfaces (aftercare, distribution,
              doctors, users, documents) effectively unreachable from the
              landing tab. Now toggleable independent of activeTab so any
              hidden surface becomes a 2-click discoverable. */}
          {secondaryNavItems.length > 0 && (showMoreNav || ['distribution', 'doctors', 'users', 'documents'].includes(activeTab)) && (
            <div className="flex space-x-1 pb-2 border-t pt-2" data-testid="nav-secondary-row">
              {secondaryNavItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => item.href ? navigate(item.href) : setActiveTab(item.id)}
                  data-testid={`nav-secondary-${item.id}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                    activeTab === item.id
                      ? 'bg-slate-200 text-slate-800'
                      : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="h-3 w-3" />
                  {item.label || t(item.labelKey)}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* CLEANUP-02 (CHANGE 1): ClinicQuotaStrip row removed — duplicate of
            the in-dashboard "Lead funnel" KPI strip below (Total Leads / New /
            … / Campaigns). The underlying `quotaSummary` data is preserved
            because the Campaigns KPI card still reads from it. */}

        {/* CLEANUP-02 (CHANGES 2 + 3): Unified Operational Summary container —
            all three KPI groups (post-acceptance state, lead funnel, team &
            messaging) live inside ONE outer card with ONE title, ONE Refresh
            button, and a collapse/expand toggle. Collapse state persists in
            localStorage under `clinic-admin.opsSummary.collapsed`. */}
        {activeTab === 'overview' && (
          <section
            className="bg-white border border-slate-200 rounded-xl mb-3"
            data-testid="operational-summary-unified"
          >
            <header className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
              <div className="min-w-0">
                <h2 className="text-[13px] font-semibold text-slate-900">Operational Summary</h2>
                <p className="text-[11px] text-slate-500 truncate">
                  Post-acceptance state · Lead funnel · Team & messaging
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleRefreshSummary}
                  disabled={summaryRefreshing}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-60 text-slate-700"
                  data-testid="operational-summary-refresh"
                >
                  <RefreshCw className={`h-3 w-3 ${summaryRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={() => setSummaryCollapsed(c => !c)}
                  aria-expanded={!summaryCollapsed}
                  aria-controls="operational-summary-body"
                  className="inline-flex items-center justify-center w-7 h-7 rounded border border-slate-200 hover:bg-slate-50 text-slate-700"
                  data-testid="operational-summary-toggle"
                  title={summaryCollapsed ? 'Expand' : 'Collapse'}
                >
                  {summaryCollapsed
                    ? <ChevronDown className="h-3.5 w-3.5" />
                    : <ChevronUp className="h-3.5 w-3.5" />
                  }
                </button>
              </div>
            </header>
            {!summaryCollapsed && (
              <div id="operational-summary-body" className="p-3 space-y-3" data-testid="operational-summary-body">
                {/* Group A — Post-acceptance state (was AdminOperationalSummaryCard, header hidden) */}
                <div data-testid="ops-group-post-acceptance">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5 px-0.5">{t('provider.adminDash.postAcceptanceState')}</p>
                  <AdminOperationalSummaryCard hideHeader refreshKey={summaryRefreshKey} />
                </div>
                {/* Group B — Lead funnel (was top-level stats grid) */}
                <div data-testid="ops-group-lead-funnel">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5 px-0.5">{t('provider.adminDash.leadFunnel')}</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                    <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                      <CardContent className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold leading-none">{funnelCounts.total}</span>
                          <span className="text-[10px] text-blue-100 leading-tight">{t('adminDashboard.totalLeads')}</span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-sky-500 to-sky-600 text-white">
                      <CardContent className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {/* DASHBOARD-CONSISTENCY-C1 — New now = human stage NEW (was the
                              quote-inbox counter, which broke funnel reconciliation). */}
                          <span
                            className="text-xl font-bold leading-none"
                            data-testid="dashboard-stat-new"
                          >
                            {funnelCounts.NEW}
                          </span>
                          <span className="text-[10px] text-sky-100 leading-tight">{t('provider.leads.stage.NEW')}</span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-indigo-600 leading-none" data-testid="dashboard-stat-working">{funnelCounts.WORKING}</span>
                          <span className="text-[10px] text-gray-500 leading-tight">{t('provider.leads.stage.WORKING')}</span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-slate-600 leading-none" data-testid="dashboard-stat-on-hold">{funnelCounts.ON_HOLD}</span>
                          <span className="text-[10px] text-gray-500 leading-tight">{t('provider.leads.stage.ON_HOLD')}</span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-emerald-700 leading-none" data-testid="dashboard-stat-won">{funnelCounts.WON}</span>
                          <span className="text-[10px] text-gray-500 leading-tight">{t('provider.leads.stage.WON')}</span>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-rose-600 leading-none" data-testid="dashboard-stat-lost">{funnelCounts.LOST}</span>
                          <span className="text-[10px] text-gray-500 leading-tight">{t('provider.leads.stage.LOST')}</span>
                        </div>
                      </CardContent>
                    </Card>
                    {/* OT-0A.1 — Campaigns KPI card. Reads count from quotaSummary, navigates to /clinic/campaigns. */}
                    <Card
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate('/clinic/campaigns')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/clinic/campaigns'); } }}
                      className="cursor-pointer hover:shadow-md hover:border-fuchsia-300 transition-all"
                      data-testid="kpi-campaigns-card"
                      title={t('provider.adminDash.manageCampaigns')}
                    >
                      <CardContent className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Megaphone className="h-4 w-4 text-fuchsia-600" />
                          <span className="text-xl font-bold text-fuchsia-700 leading-none" data-testid="kpi-campaigns-count">
                            {quotaSummary?.campaigns?.active_current ?? 0}
                          </span>
                          <span className="text-[10px] text-gray-500 leading-tight">{t('provider.adminDash.campaigns')}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
                {/* Group C — Team & messaging (was OperationalWidgets, internal refresh hidden) */}
                <div data-testid="ops-group-team-messaging">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5 px-0.5">{t('provider.adminDash.teamMessaging')}</p>
                  <OperationalWidgets refreshKey={widgetRefreshKey + summaryRefreshKey * 1000} hideRefresh />
                </div>
              </div>
            )}
          </section>
        )}

        {/* Overview Tab — remaining sections (analytics, marketplace etc.) */}
        {activeTab === 'overview' && (
          <div className="space-y-3">
            {/* TREATX-PROVIDER-ADMIN-DASHBOARD-WIDGET-FLOW-01:
                Removed the visible Operations / Analytics / Executive
                preset selector and the executive-only conversion-rate
                badge from the dashboard. All widgets below now render
                unconditionally in a continuous, workflow-oriented
                vertical layout. */}

            {presetVis.analyticsVisible && clinicId && (
              <CollapsibleAnalytics
                conversionRate={analyticsSummary.conversionRate}
                conversionDelta={analyticsSummary.conversionDelta}
                totalLeads={analyticsSummary.totalLeads}
                wonLeads={analyticsSummary.wonLeads}
                lostLeads={analyticsSummary.lostLeads}
                forceExpanded={presetVis.analyticsExpanded}
                forceCollapsed={!presetVis.analyticsExpanded && layoutPreset === 'operations'}
              >
                <AnalyticsFilterBar
                  clinicId={clinicId}
                  filters={analyticsFilters}
                  onChange={setAnalyticsFilters}
                />
                <ProviderPerformancePanel
                  clinicId={clinicId}
                  filters={analyticsFilters}
                  onSummaryUpdate={perfSummaryUpdate}
                />
                <ConversionTrendPanel
                  clinicId={clinicId}
                  filters={analyticsFilters}
                  onSummaryUpdate={trendSummaryUpdate}
                />
              </CollapsibleAnalytics>
            )}

            {/* P2-2: Suspension Notice Card - Shows details when suspended */}
            {isSuspended && suspensionDetails && (
              <SuspensionReasonCard suspensionDetails={suspensionDetails} />
            )}

            {/* P0.1 FIX: Removed P2-1 Package Usage & Limits Warning that used hardcoded PackageEnforcementContext data */}
            {/* All usage stats now come from SubscriptionContext (subscription.packageSnapshot.limits) */}
            
            {/* Sprint 10: Subscription-based limit warnings (replaces P2-1 when subscription is active) */}
            {subscription && packageSnapshot && checkUsageLimit && (
              <div className="space-y-3">
                {/* Leads limit warning */}
                {(() => {
                  const leadsCheck = checkUsageLimit('leads_per_month');
                  if (leadsCheck.atLimit) {
                    return (
                      <UsageLimitBanner 
                        limitType="leads"
                        current={leadsCheck.current}
                        max={leadsCheck.limit}
                        onUpgrade={onUpgrade}
                      />
                    );
                  }
                  if (leadsCheck.nearLimit) {
                    return (
                      <UsageLimitBanner 
                        limitType="leads"
                        current={leadsCheck.current}
                        max={leadsCheck.limit}
                        onUpgrade={onUpgrade}
                        onDismiss={() => {}} // Allow dismissing near-limit warnings
                      />
                    );
                  }
                  return null;
                })()}
                
                {/* Sales Reps limit warning */}
                {(() => {
                  const repsCheck = checkUsageLimit('sales_reps');
                  if (repsCheck.atLimit) {
                    return (
                      <UsageLimitBanner 
                        limitType="salesReps"
                        current={repsCheck.current}
                        max={repsCheck.limit}
                        onUpgrade={onUpgrade}
                      />
                    );
                  }
                  if (repsCheck.nearLimit) {
                    return (
                      <UsageLimitBanner 
                        limitType="salesReps"
                        current={repsCheck.current}
                        max={repsCheck.limit}
                        onUpgrade={onUpgrade}
                        onDismiss={() => {}}
                      />
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {/* TREATX-PROVIDER-ADMIN-DASHBOARD-WIDGET-FLOW-01:
                Widgets below are now rendered unconditionally in a
                fixed workflow order: Lead Funnel → Marketplace +
                Clinic-Owned Leads → Plan Usage. The previous
                maxHeight/opacity transition wrappers (driven by the
                retired preset switcher) have been replaced with plain
                wrappers — the `data-testid` hooks are preserved so any
                existing automation continues to work. Widget internals
                are unchanged. */}

            {/* Sprint 9: Lead Funnel Dashboard */}
            <div data-testid="leads-table-section">
              <LeadFunnel 
                leads={leads} 
                title={t('adminDashboard.leadFunnel')} 
                showSourceBreakdown={true}
              />
            </div>

            {/* Lead Source Stats — TreatX Marketplace + Clinic-Owned */}
            <div data-testid="source-cards-section">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-purple-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Globe className="h-5 w-5 text-purple-600" />
                      TreatX Marketplace Leads
                    </CardTitle>
                    <CardDescription>{t('provider.adminDash.leadsFromTreatX')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-3xl font-bold text-purple-600">{sourceCounts.marketplace.total}</p>
                        <p className="text-sm text-gray-500">{t('provider.adminDash.newThisWeek', { count: sourceCounts.marketplace.new })}</p>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700" data-testid="marketplace-commission-badge">{t('provider.adminDash.commissionPerContract')}</Badge>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-amber-600" />
                      {t('provider.adminDash.clinicOwnedLeads')}
                    </CardTitle>
                    <CardDescription>{t('provider.adminDash.ownSourcedPatients')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-3xl font-bold text-amber-600">{sourceCounts.providerOwned.total}</p>
                        <p className="text-sm text-gray-500">{t('provider.adminDash.newThisWeek', { count: sourceCounts.providerOwned.new })}</p>
                      </div>
                      {/* P2-2: Disable Add Patient when suspended */}
                      {isSuspended ? (
                        <DisabledSuspendedButton data-testid="add-patient-btn-disabled">{t('provider.adminDash.addPatient')}</DisabledSuspendedButton>
                      ) : (
                        <Button onClick={() => setShowAddLeadModal(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="add-patient-btn">
                          <UserPlus className="h-4 w-4 mr-2" />
                          {t('provider.adminDash.addPatient')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Plan Usage (UsageStatsCardNew) */}
            <div data-testid="quick-actions-section">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Sprint 10 Batch 2.1: Subscription-Based Usage Stats with Real-Time Tracking
                    Single source of truth for usage/limits (driven by SubscriptionContext). */}
                {subscription && packageSnapshot && (
                  <UsageStatsCardNew 
                    usage={planUsage}
                    limits={packageSnapshot.limits}
                    packageName={packageSnapshot.name}
                    onUpgrade={onUpgrade}
                    realTimeUsage={null}
                    onRefresh={onRefreshUsage}
                    isRefreshing={isRefreshingUsage}
                  />
                )}
              </div>
            </div>

            {/* CLEANUP-01: Provider Modules section removed per dashboard cleanup task.
                Cards previously here (Doctor Affiliations, Campaigns, Before/After,
                Clinic Logo, Facility Photos, Documents, Treatment Plans, Notifications)
                remain reachable via the existing top-nav menus (Doctor Affiliations
                tab on this page, More menu, etc.). Routes/pages are unchanged. */}

            {/* CLEANUP-01: Premium Features section removed per dashboard cleanup task
                (Bulk Operations / Activity Timeline / Advanced Reports / API Access)
                — not part of current product offering. */}

            {/* TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02:
                Group "New Leads (Unassigned)" + "Sales Team Performance"
                into a single visual container with one collapse/expand
                control. Widget internals are unchanged — purely an
                outer frame + collapse. */}
            <Card data-testid="new-leads-sales-perf-section">
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-base font-semibold text-slate-800">
                    {t('adminDashboard.newLeadsUnassigned')} · {t('adminDashboard.salesTeamPerformance')}
                  </CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setNewLeadsSalesCollapsed(prev => !prev)}
                  data-testid="new-leads-sales-perf-toggle"
                  aria-expanded={!newLeadsSalesCollapsed}
                  aria-label={newLeadsSalesCollapsed ? 'Expand section' : 'Collapse section'}
                >
                  {newLeadsSalesCollapsed
                    ? <ChevronDown className="h-4 w-4" />
                    : <ChevronUp className="h-4 w-4" />}
                </Button>
              </CardHeader>
              {!newLeadsSalesCollapsed && (
                <CardContent data-testid="new-leads-sales-perf-body">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent New Leads */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <AlertCircle className="h-5 w-5 text-green-500" />
                          {t('adminDashboard.newLeadsUnassigned')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {leads.filter(l => l.status === 'NEW').slice(0, 5).length > 0 ? (
                          <div className="space-y-3">
                            {leads.filter(l => l.status === 'NEW').slice(0, 5).map(lead => {
                              const patient = getPatientForLead(lead);
                              return (
                                <div key={lead.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div>
                                    <p className="font-medium">{patient.name}</p>
                                    <p className="text-sm text-gray-500" data-testid={`admin-lead-treatment-${lead.id}`}>{lead.treatment_display_name || lead.treatment_interest || lead.treatment}</p>
                                  </div>
                                  <Button size="sm" onClick={() => setActiveTab('leads')}>
                                    <Eye className="h-4 w-4 mr-1" />
                                    {t('adminDashboard.view', 'View')}
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-4">{t('adminDashboard.noNewUnassigned')}</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Sales Team Performance */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-blue-500" />
                          {t('adminDashboard.salesTeamPerformance')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {salesReps.map(rep => {
                            const repLeads = leads.filter(l => l.assigned_agent_id === rep.rep_id);
                            // DASHBOARD-CONSISTENCY-C1 — "active" = non-terminal human stages
                            // (NEW/WORKING/ON_HOLD); LOST/WON are excluded so the label matches
                            // the number. Conversion uses the human WON stage.
                            const repActive = repLeads.filter(l => !['WON', 'LOST'].includes(l.status_transitions?.human_stage));
                            const repWon = repLeads.filter(l => l.status_transitions?.human_stage === 'WON');
                            return (
                              <div key={rep.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <img src={rep.avatar} alt="" className="w-10 h-10 rounded-full" />
                                <div className="flex-1">
                                  <p className="font-medium">{rep.name}</p>
                                  <p className="text-sm text-gray-500">{t('adminDashboard.activeLeads', { count: repActive.length })}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-green-600">
                                    {repLeads.length > 0
                                      ? Math.round((repWon.length / repLeads.length) * 100)
                                      : 0}%
                                  </p>
                                  <p className="text-xs text-gray-500">{t('adminDashboard.conversion')}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* TREATX-PROVIDER-ADMIN-IA-CONSOLIDATION-02:
                Aftercare Overview — embedded EXISTING AfterSalesDashboard
                component verbatim (same source-of-truth used at
                /clinic/aftercare). Wrapped in a collapsible section with
                an outer Refresh that remounts via a key bump (no rewrite
                of Aftercare logic). */}
            <Card data-testid="aftercare-overview-section">
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <HeartPulse className="h-5 w-5 text-emerald-600" />
                    Aftercare Overview
                  </CardTitle>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAftercareEmbedRefreshKey(k => k + 1)}
                    data-testid="aftercare-overview-refresh"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    Refresh
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAftercareEmbedCollapsed(prev => !prev)}
                    data-testid="aftercare-overview-toggle"
                    aria-expanded={!aftercareEmbedCollapsed}
                    aria-label={aftercareEmbedCollapsed ? 'Expand Aftercare Overview' : 'Collapse Aftercare Overview'}
                  >
                    {aftercareEmbedCollapsed
                      ? <ChevronDown className="h-4 w-4" />
                      : <ChevronUp className="h-4 w-4" />}
                  </Button>
                </div>
              </CardHeader>
              {!aftercareEmbedCollapsed && (
                <CardContent className="p-0" data-testid="aftercare-overview-body">
                  <AfterSalesDashboard key={aftercareEmbedRefreshKey} />
                </CardContent>
              )}
            </Card>
          </div>
        )}

        {/* Lead Management Tab */}
        {activeTab === 'leads' && (
          <LeadManagement 
            clinicId={clinicId}
            onRefresh={loadData}
            focusLeadId={routeLeadId}
          />
        )}

        {/* Offers Overview Tab (Item 5) */}
        {activeTab === 'offers' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{t('offersOverview.title')}</h2>
                <p className="text-slate-500">{t('offersOverview.subtitle')}</p>
              </div>
              <Badge className="bg-blue-100 text-blue-700">
                {t('offersOverview.totalOffers', { count: offers.length })}
              </Badge>
            </div>

            {/* Offer Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold text-blue-600">
                      {offers.filter(o => o.status === 'SENT' || o.status === 'PENDING').length}
                    </span>
                    <span className="text-xs text-gray-500">{t('offersOverview.activeOffers')}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold text-green-600">
                      {offers.filter(o => o.status === 'ACCEPTED').length}
                    </span>
                    <span className="text-xs text-gray-500">{t('offersOverview.accepted')}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold text-amber-600">
                      {offers.filter(o => o.status === 'PENDING_APPROVAL').length}
                    </span>
                    <span className="text-xs text-gray-500">{t('offersOverview.pendingApproval')}</span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col" data-testid="offers-total-value">
                    {latestOfferTotals.length === 0 ? (
                      <span className="text-2xl font-bold text-slate-600">—</span>
                    ) : (
                      latestOfferTotals.map(([cur, sum]) => (
                        <span key={cur} className="text-lg font-bold text-slate-600 leading-tight" data-testid={`offers-total-${cur}`}>
                          {cur} {sum.toLocaleString()}
                        </span>
                      ))
                    )}
                    <span className="text-xs text-gray-500">{t('offersOverview.totalValueLatest')}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* BATCH B1 — Latest/History scope + search + filters toolbar */}
            <div className="flex flex-wrap items-center gap-2" data-testid="offers-scope-tabs">
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                <button
                  type="button"
                  onClick={() => setOfferScope('all')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${offerScope === 'all' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  data-testid="offers-scope-all"
                >
                  {t('offersOverview.scopeAll')} ({allCount})
                </button>
                <button
                  type="button"
                  onClick={() => setOfferScope('latest')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${offerScope === 'latest' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  data-testid="offers-scope-latest"
                >
                  {t('offersOverview.scopeLatest')} ({latestCount})
                </button>
                <button
                  type="button"
                  onClick={() => setOfferScope('history')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${offerScope === 'history' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  data-testid="offers-scope-history"
                >
                  {t('offersOverview.scopeHistory')} ({historyCount})
                </button>
              </div>
            </div>

            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-end gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('offersOverview.searchLabel')}</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={offerSearch}
                        onChange={(e) => setOfferSearch(e.target.value)}
                        placeholder={t('offersOverview.searchPlaceholder')}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="offers-search-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('provider.adminDash.colStatus')}</label>
                    <select value={offerFilters.status} onChange={(e) => setOfferFilters(f => ({ ...f, status: e.target.value }))} className="py-2 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="offers-filter-status">
                      <option value="ALL">{t('offersOverview.allStatuses')}</option>
                      {offerFilterOptions.statuses.map(s => (
                        <option key={s} value={s}>{(OfferCard.STATUS_CONFIG[s]?.label) || s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('provider.adminDash.colSalesRep')}</label>
                    <select value={offerFilters.salesRep} onChange={(e) => setOfferFilters(f => ({ ...f, salesRep: e.target.value }))} className="py-2 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="offers-filter-rep">
                      <option value="ALL">{t('offersOverview.allReps')}</option>
                      {offerFilterOptions.reps.map(([id, name]) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('provider.adminDash.colTreatment')}</label>
                    <select value={offerFilters.treatment} onChange={(e) => setOfferFilters(f => ({ ...f, treatment: e.target.value }))} className="py-2 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="offers-filter-treatment">
                      <option value="ALL">{t('offersOverview.allTreatments')}</option>
                      {offerFilterOptions.treatments.map(tr => (
                        <option key={tr} value={tr}>{tr}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('offersOverview.dateFrom')}</label>
                    <input type="date" lang={dateInputLang(i18n.language)} value={offerFilters.dateFrom} onChange={(e) => setOfferFilters(f => ({ ...f, dateFrom: e.target.value }))} className="py-2 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="offers-filter-date-from" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('offersOverview.dateTo')}</label>
                    <input type="date" lang={dateInputLang(i18n.language)} value={offerFilters.dateTo} onChange={(e) => setOfferFilters(f => ({ ...f, dateTo: e.target.value }))} className="py-2 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="offers-filter-date-to" />
                  </div>
                  {offerFiltersActive && (
                    <Button variant="ghost" size="sm" onClick={resetOfferFilters} data-testid="offers-filter-clear" className="text-slate-500">
                      <X className="h-4 w-4 mr-1" /> {t('offersOverview.clearFilters')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Offers Table - Using standardized OfferCard (6B-12) */}
            {/* Sprint 7 Batch 3: Added sortable headers | BATCH B1: search/filter/sort/scope */}
            <Card>
              <CardHeader>
                <CardTitle>{offerScope === 'all' ? t('offersOverview.allOffers') : offerScope === 'history' ? t('offersOverview.historyOffers') : t('offersOverview.latestOffers')}</CardTitle>
                <CardDescription>
                  {t('offersOverview.showingCount', { shown: visibleOffers.length, total: offerScope === 'all' ? allCount : offerScope === 'history' ? historyCount : latestCount })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {visibleOffers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th 
                            className="text-left p-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-50 select-none"
                            onClick={() => toggleOfferSort('patientName')}
                            data-testid="sort-patient"
                          >
                            {t('provider.adminDash.colPatient', 'Patient')}
                            {renderSortIndicator('patientName')}
                          </th>
                          <th 
                            className="text-left p-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-50 select-none"
                            onClick={() => toggleOfferSort('treatment')}
                            data-testid="sort-treatment"
                          >
                            {t('provider.adminDash.colTreatment')}
                            {renderSortIndicator('treatment')}
                          </th>
                          <th 
                            className="text-left p-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-50 select-none"
                            onClick={() => toggleOfferSort('salesRep')}
                            data-testid="sort-rep"
                          >
                            {t('provider.adminDash.colSalesRep')}
                            {renderSortIndicator('salesRep')}
                          </th>
                          <th 
                            className="text-right p-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-50 select-none"
                            onClick={() => toggleOfferSort('totalPrice')}
                            data-testid="sort-amount"
                          >
                            {t('provider.adminDash.amount')}
                            {renderSortIndicator('totalPrice')}
                          </th>
                          <th 
                            className="text-center p-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-50 select-none"
                            onClick={() => toggleOfferSort('status')}
                            data-testid="sort-status"
                          >
                            {t('provider.adminDash.colStatus')}
                            {renderSortIndicator('status')}
                          </th>
                          <th 
                            className="text-center p-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-50 select-none"
                            onClick={() => toggleOfferSort('createdAt')}
                            data-testid="sort-created"
                          >
                            {t('provider.adminDash.created')}
                            {renderSortIndicator('createdAt')}
                          </th>
                          <th className="text-center p-3 text-sm font-medium text-slate-600">{t('provider.adminDash.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleOffers.map(offer => (
                          <OfferCard.TableRow
                            key={offer.id}
                            offer={offer}
                            role="provider_admin"
                            salesReps={salesReps}
                            onViewDetails={setSelectedOffer}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : offers.length > 0 ? (
                  <div className="text-center py-12 text-slate-500" data-testid="offers-no-results">
                    <Search className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>{t('offersOverview.noResults')}</p>
                    {(offerFiltersActive || offerScope === 'history') && (
                      <Button variant="outline" size="sm" className="mt-3" onClick={() => { resetOfferFilters(); setOfferScope('latest'); }} data-testid="offers-no-results-reset">
                        {t('offersOverview.clearFilters')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>{t('provider.adminDash.noOffersYet')}</p>
                    <p className="text-sm">{t('provider.adminDash.salesRepsCanCreate')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Offer Detail Modal - Using standardized OfferCard.Detailed (6B-12) */}
            {selectedOffer && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold">{t('provider.adminDash.offerDetails')}</h3>
                      <p className="text-sm text-slate-500">{t('provider.adminDash.ref', { id: selectedOffer.id })}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setSelectedOffer(null)}>
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="p-6">
                    <OfferCard.Detailed
                      offer={{
                        ...selectedOffer,
                        createdByName: salesReps.find(r => r.id === selectedOffer.createdBy)?.name
                      }}
                      role="provider_admin"
                      onClose={() => setSelectedOffer(null)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lead Distribution Tab */}
        {activeTab === 'distribution' && (
          <div className="space-y-6">
            {/* TREATX-PROVIDER-META-IA-RELOCATION-03: full Meta/WhatsApp
                configuration moved to Settings → Integrations. The Leads
                screen keeps only a lightweight CTA to the canonical
                Integrations location (no Connect/Disconnect/asset controls). */}
            <Card data-testid="leads-meta-integration-cta">
              <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-slate-100 text-slate-600 shrink-0">
                    <Plug className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{t('provider.metaConnect.leadsCtaTitle')}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t('provider.metaConnect.leadsCtaBody')}</div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab('integrations')}
                  data-testid="leads-manage-integration-btn"
                >
                  {t('provider.metaConnect.leadsCtaManage')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
            <LeadDistribution 
              clinicId={clinicId}
              onRefresh={loadData}
            />
          </div>
        )}

        {activeTab === 'integrations' && (
          <section className="space-y-4" data-testid="integrations-section">
            <div>
              <h2 className="text-lg font-semibold text-slate-900" data-testid="integrations-title">
                {integrationProvider === 'meta'
                  ? t('provider.metaConnect.integrationsTitle')
                  : integrationProvider === 'paytr'
                    ? t('provider.paytr.title')
                    : t('provider.pspOnboarding.integrationsTitle')}
              </h2>
              <p className="text-sm text-slate-500">
                {integrationProvider === 'meta'
                  ? t('provider.metaConnect.integrationsSubtitle')
                  : integrationProvider === 'paytr'
                    ? t('provider.paytr.subtitle')
                    : t('provider.pspOnboarding.integrationsSubtitle')}
              </p>
            </div>
            {/* O78 P1 — one Integrations area, two providers. The iyzico
                section REUSES PaymentAccountOnboardingCard and the existing
                /api/clinic/iyzico/* endpoints: no duplicate onboarding
                service, provider identity record or finance dashboard. */}
            <div className="flex items-center gap-1 border-b border-slate-200" role="tablist">
              {[
                { key: 'meta', label: t('provider.metaConnect.tabMeta') },
                { key: 'iyzico', label: t('provider.metaConnect.tabIyzico') },
                { key: 'paytr', label: t('provider.metaConnect.tabPaytr') },
              ].map((tab) => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={integrationProvider === tab.key}
                  onClick={() => setIntegrationProvider(tab.key)}
                  className={`px-3 py-2 text-sm transition-colors ${
                    integrationProvider === tab.key
                      ? 'border-b-2 border-teal-600 text-teal-700 font-medium'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`integrations-tab-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {integrationProvider === 'meta' && <MetaConnectionCard />}
            {integrationProvider === 'iyzico' && (
              <div className="space-y-4 min-w-0" data-testid="integrations-iyzico-panel">
                <PaymentAccountOnboardingCard showFinanceLink returnTo="integrations" />
                {/* MULTICCY P0 — the per-bank-account settlement surface belongs
                    on the OWNER-FACING Integrations screen, not only on the
                    profile editor. Same component, same
                    /api/clinic/settlement-accounts projection and the same
                    per-account registration action: no duplicate logic. */}
                <SettlementAccountsCard />
              </div>
            )}
            {/* MULTI-PSP — PayTR sits immediately after iyzico and is entirely
                independent of it: its own readiness call, its own capability
                registry and its own account state. A blocked PayTR tab can
                never disable or interfere with iyzico. */}
            {integrationProvider === 'paytr' && (
              <div className="space-y-4 min-w-0" data-testid="integrations-paytr-panel">
                <PayTRIntegrationCard />
              </div>
            )}
          </section>
        )}

        {/* Clinic Profile Tab — REMOVED.
            Provider Profile is now the single source of truth at
            /#/clinic/profile. The Profile nav item above navigates there
            directly (href='/clinic/profile'), so the inline legacy surface
            (previously backed by admin/ClinicProfileEditor) is no longer
            rendered or reachable from the dashboard. */}

        {/* Treatment Management Tab */}
        {activeTab === 'treatments' && (
          <TreatmentManagement 
            clinicId={clinicId}
            clinic={clinic}
          />
        )}

        {/* Doctor Management Tab */}
        {activeTab === 'doctors' && (
          <DoctorManagement 
            clinicId={clinicId}
          />
        )}

        {/* Sales Rep Management Tab — OWNER-TEST-BLOCKER-FIX-PACK:
            staff onboarding is via canonical Team / Invitations only. */}
        {activeTab === 'sales-reps' && (
          <SalesRepManagement 
            clinicId={clinicId}
            salesReps={salesReps}
            leads={leads}
          />
        )}

        {/* User Management Tab - Navigate to dedicated page */}
        {activeTab === 'users' && (
          <Card>
            <CardContent className="py-12 text-center">
              <UserPlus className="h-16 w-16 text-teal-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('provider.adminDash.userManagement')}</h3>
              <p className="text-gray-500 mb-6">
                {t('provider.adminDash.manageTeamHelp')}
              </p>
              <Button onClick={() => navigate('/clinic/admin/users')} className="bg-teal-600 hover:bg-teal-700">
                {t('provider.adminDash.openUserManagement')}
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Document Management Tab */}
        {activeTab === 'documents' && (
          <DocumentManagement 
            clinicId={clinicId}
          />
        )}
      </main>
      
      {/* Build Version Stamp - For deployment verification */}
      <div className="fixed bottom-4 right-4 bg-gray-800 text-white text-xs px-3 py-1 rounded-full opacity-75 z-50" data-testid="build-stamp">
        Build: sprint10-v10.6.0
      </div>
      
      {/* P0-015 FIX: AddLeadModal rendering — was imported but never rendered */}
      <AddLeadModal
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onCreated={async (formData) => {
          await apiCreateLead(formData);
          fetchApiLeads();
        }}
        clinicId={clinicId}
        salesReps={salesReps}
      />
    </div>
  );
};

// REMOVED: ClinicAdminDashboardInner (dead code — duplicated logic now lives in ClinicAdminDashboardWithSubscription)

// Wrapper with SubscriptionProvider — ALWAYS renders, never blocks on sessionStorage
const ClinicAdminDashboard = () => {
  const { t } = useTranslation();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId, isClinicAdmin } = useClinicScope();
  const { isAuthenticated } = useRBAC();
  const resolvedClinicId = rbacClinicId || clinicUser?.clinicId;

  // If we have a clinicId (from RBAC localStorage or clinicUser sessionStorage), wrap with subscription
  if (resolvedClinicId) {
    return (
      <SubscriptionProvider clinicId={resolvedClinicId}>
        <ClinicAdminDashboardWithSubscription />
      </SubscriptionProvider>
    );
  }

  // RBAC is authenticated but clinicId not yet resolved — show loading (ClinicDataContext will hydrate)
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="clinic-admin-loading-wrapper">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-sm text-slate-500">{t('provider.adminDash.resolvingContext')}</p>
        </div>
      </div>
    );
  }

  // Not authenticated at all — show loading (redirect will happen from RBAC route guard)
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="clinic-admin-not-auth">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400 mx-auto mb-3"></div>
        <p className="text-sm text-slate-500">Checking authentication...</p>
      </div>
    </div>
  );
};

// Wrapper that tries to pass subscription props but NEVER blocks rendering
const ClinicAdminDashboardWithSubscription = () => {
  const { clinicUser } = useClinicData();
  const { 
    subscription, trialStatus, trialExpired,
    upgradeSubscription,
    canUseFeature, packageSnapshot, usage,
    checkUsageLimit, canPerformAction, loading 
  } = useSubscription();
  
  const { usage: realTimeUsage, syncUsage } = useUsageTracking();
  const [isRefreshingUsage, setIsRefreshingUsage] = useState(false);
  const [showTrialWarning, setShowTrialWarning] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitModalData, setLimitModalData] = useState({ limitType: 'leads', current: 0, max: 0 });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgradePackage, setSelectedUpgradePackage] = useState(null);

  // MTMR-P0 B1.1 — the Provider-side trial auto-start was REMOVED.
  // Opening/refreshing the Provider Dashboard must never create or extend
  // a subscription. The launch trial is provisioned server-side at the
  // canonical Provider activation transition. This dashboard is a
  // CONSUMER of subscription state, not its creator.

  useEffect(() => {
    if (trialStatus?.isLastDay && !trialExpired && subscription?.packageId === 'TRIAL') {
      const warningShown = sessionStorage.getItem('trialWarningShown');
      if (!warningShown) { setShowTrialWarning(true); sessionStorage.setItem('trialWarningShown', 'true'); }
    }
  }, [trialStatus, trialExpired, subscription]);
  
  const handleRefreshUsage = async () => {
    setIsRefreshingUsage(true); syncUsage?.(); setTimeout(() => setIsRefreshingUsage(false), 500);
  };
  const handleLimitBlocked = (limitType, current, max) => { setLimitModalData({ limitType, current, max }); setShowLimitModal(true); };
  const handleUpgrade = () => { 
    // Navigate to full package selection page — no hardcoded package assumption
    window.location.hash = '#/clinic/select-plan'; 
  };
  const handleUpgradeComplete = async (packageId, paymentInfo) => {
    try {
      await upgradeSubscription(packageId, paymentInfo);
      toast({ title: 'Plan Upgraded!', description: `You're now on the ${packageId} plan.` });
    } catch (error) {
      toast({ title: 'Upgrade Failed', description: error.message, variant: 'destructive' });
    }
  };

  // ALWAYS render the dashboard — subscription is enhancement, not gate
  return (
    <>
      <ClinicAdminDashboardContent 
        subscription={subscription} 
        trialStatus={trialStatus} 
        canUseFeature={canUseFeature}
        packageSnapshot={packageSnapshot}
        usage={usage}
        checkUsageLimit={checkUsageLimit}
        checkSubscriptionAction={canPerformAction}
        onShowTrialWarning={() => setShowTrialWarning(true)}
        onLimitBlocked={handleLimitBlocked}
        onUpgrade={handleUpgrade}
        realTimeUsage={realTimeUsage}
        onRefreshUsage={handleRefreshUsage}
        isRefreshingUsage={isRefreshingUsage}
      />
      {showTrialWarning && (
        <TrialExpiryWarningModal
          trialStatus={trialStatus}
          onDismiss={() => setShowTrialWarning(false)}
          onUpgrade={() => { setShowTrialWarning(false); handleUpgrade(); }}
        />
      )}
      {showLimitModal && (
        <UsageLimitModal
          isOpen={true}
          onClose={() => setShowLimitModal(false)}
          limitType={limitModalData.limitType}
          current={limitModalData.current}
          max={limitModalData.max}
          onUpgrade={handleUpgrade}
        />
      )}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={true}
          onClose={() => setShowUpgradeModal(false)}
          selectedPackageId={selectedUpgradePackage}
          currentPackageId={subscription?.packageId}
          onUpgradeComplete={handleUpgradeComplete}
        />
      )}
    </>
  );
};

export default ClinicAdminDashboard;
